import request from 'utils/axios';
import { AGENCY_MANAGEMENT_BASE_URL, PERSON_MANAGEMENT_BASE_URL } from 'configs';
import { IChangePasswordRequest } from './types';
import { ISingleTeamMemberResponse } from '../TeamMember/types';

export interface IAgencyProfileResponse {
  id: number;
  agencyName: string;
  tradeName: string;
  agencyWebsite: string;
  license: {
    licenseNumber: string;
    licenseType: string;
    licenseExpiryDate: string;
  };
  countryId?: number;
  registrationNumber: string;
  address: string;
  agencyLogo: string;
  preferredLanguage: string;
  isTripExist: boolean;
  currencyCode: string;
  currencyCodeIso?: string;
}

export interface IUpdateAgencyProfileRequest {
  agencyName: string;
  countryId: number;
  registrationNumber: string;
  address: string;
  profilePicture?: File;
}

export interface IUpdateProfileRequest {
  phoneCountryIso: string;
  phoneCountryCode: string;
  contactNumber: string;
  ssoEnabled: boolean;
  agentId: number;
  agencyId: number;
  companyId: number;
}

/**
 * Get agency profile data
 * @param agencyId - The agency ID
 * @returns Promise with agency profile data
 */
export const getAgencyProfile = async (agencyId: string | number): Promise<IAgencyProfileResponse> => {
  const url = `${AGENCY_MANAGEMENT_BASE_URL}/api/v1/agency/${agencyId}`;
  const { data } = await request({ url, method: 'GET' });
  return data;
};

/**
 * Update agency profile data
 * @param agencyId - The agency ID
 * @param formData - Form data containing agency profile information
 * @returns Promise with updated agency profile data
 */
export const updateAgencyProfile = async (
  agencyId: string | number,
  formData: FormData,
): Promise<IAgencyProfileResponse> => {
  const url = `${AGENCY_MANAGEMENT_BASE_URL}/api/v1/agency/${agencyId}`;
  const { data } = await request({ url, method: 'PUT', data: formData });
  return data;
};

/**
 * Change user password
 * @param passwordData - Object containing current and new password
 * @returns Promise with success response
 */
export const changePassword = async (passwordData: IChangePasswordRequest): Promise<void> => {
  const url = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/users/change-password`;
  await request({
    url,
    method: 'PUT',
    data: passwordData,
    customHeaders: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
};

/**
 * Update user profile data
 * @param formData - Form data containing profile information and optional profile picture
 * @returns Promise with updated profile data
 */
export const updateProfile = async (formData: FormData): Promise<ISingleTeamMemberResponse> => {
  const url = `${PERSON_MANAGEMENT_BASE_URL}/api/v1/profile`;
  const { data } = await request({
    url,
    method: 'PUT',
    data: formData,
    customHeaders: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

/**
 * Delete agency
 * @param agencyId - The agency ID to delete
 * @returns Promise with success response
 */
export const deleteAgency = async (agencyId: string | number): Promise<void> => {
  const url = `${AGENCY_MANAGEMENT_BASE_URL}/api/v1/agency/${agencyId}`;
  await request({ url, method: 'DELETE' });
};
